/**
 * Explore search bar hints — the shared definition of the
 * `explore_search_hint_order` / `explore_search_hint_kind` fields on the
 * Masterlist basic forms and the Post form. Redmine #265828.
 *
 * Same shape as constants/exploreCarousel.js, with one difference: the pool size
 * is NOT a build-time env value. It lives in the backend's app_configs
 * collection (key EXPLORE_SEARCH_HINTS_SIZE) and can change without a deploy, so
 * it is read at runtime from GET /explore/search-hints (`pool_size`) by
 * useSearchHintPoolSize(). Until that call returns (or if it fails) the form
 * shows no upper bound and the backend — always the authority — rejects an
 * out-of-range or duplicate slot with a message naming the valid range or the
 * record that holds it.
 */
import { useEffect, useState } from "react";
import api from "../services/api";
import { APIS } from "../endpoints";

let poolSize = null;
let pending = null;

const fetchPoolSize = () => {
  if (!pending) {
    pending = api
      .get(`${APIS.BASE_URL}${APIS.explore_search_hints}`, {
        params: { tab: "explore" },
      })
      .then((res) => {
        const n = Number(res?.data?.pool_size);
        poolSize = Number.isInteger(n) && n > 0 ? n : null;
        return poolSize;
      })
      .catch(() => null)
      .finally(() => {
        // Re-read on the next form mount, so an app_configs change shows up
        // without a page reload.
        pending = null;
      });
  }
  return pending;
};

/**
 * Current pool size (or null while unknown). Call once in each form component;
 * it re-renders the form when the value arrives so the schema builders below
 * pick it up.
 */
export const useSearchHintPoolSize = () => {
  const [size, setSize] = useState(poolSize);
  useEffect(() => {
    let active = true;
    fetchPoolSize().then((n) => {
      if (active && n !== null) setSize(n);
    });
    return () => {
      active = false;
    };
  }, []);
  return size;
};

const rangeText = () =>
  poolSize ? `1–${poolSize}` : "1 to the configured pool size";

/**
 * The JSON Schema fragment for the slot. Nullable on purpose: clearing the
 * field is how a record is REMOVED from the hint pool.
 */
export const exploreSearchHintOrderSchema = () => ({
  type: ["integer", "null"],
  title: "Explore Search Hint Order",
  minimum: 1,
  ...(poolSize ? { maximum: poolSize } : {}),
  description:
    `Assign a unique slot from ${rangeText()} to show this record as a search ` +
    `bar hint. Leave empty to keep it out. Each slot may be used by only one record.`,
});

/**
 * ui:emptyValue is null, not "" -- an empty string would reach the backend as a
 * non-integer and be rejected.
 */
export const exploreSearchHintOrderUiSchema = () => ({
  "ui:emptyValue": null,
  "ui:placeholder": `${rangeText()}, or leave empty`,
});

export const exploreSearchHintKindSchema = (
  fallbackLabel = "the Masterlist Type",
) => ({
  // Nullable: the backend stores an empty label as null.
  type: ["string", "null"],
  title: "Explore Search Hint Label",
  description:
    `Short label shown beside the name in the search hint (e.g. "Playback Singer"). ` +
    `Leave empty to use ${fallbackLabel}.`,
});

export const exploreSearchHintKindUiSchema = () => ({
  "ui:emptyValue": "",
});
